import { ReactElement, useEffect, useState } from 'react'
import {
    Button,
    Box,
    Typography,
    Icon,
    IconButton,
    FormControlLabel,
    Checkbox,
} from '@mui/material'
import { HighlightOffSharp, ShareOutlined } from '@mui/icons-material'
import {
    fetchAllLogos,
    fetchSelectedLogo,
    fetchSharedOrgsByTemId,
    updateTemplate,
} from '../../../services/dtServices'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { GetSharedOrganizationsObj } from '../../../services/apiInterfaces'
import { enqueueSnackbar } from 'notistack'
import {
    TemplateProperties,
    UserPermissions,
} from '../../../constants/constants'
import { getEntities } from '../../../redux/slices/navSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { findEntityById } from '../../../utils/EntityUtils'
import ShareTemplate from './ShareTemplate'
import theme from '../../../theme/theme'
import SettingPermisons from './SettingPermisons'
import { TemplatePermissions } from '../interfaces'
import { useTranslation } from 'react-i18next'

export interface ILogo {
    label: string
    url: string
    uuid: string
    selected?: boolean
}

const Settings = (): ReactElement => {
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)

    const navList = useAppSelector((state) => state.nav.navList)

    const permissions = useAppSelector((state) => state.org.permissions)
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)

    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )
    const entityId = selectedEntity?.id || ''
    const orgId = org?.id ? org?.id : ''

    const dispatch = useAppDispatch()

    const [exsitingSharedOrgs, setExsitingSharedOrgs] = useState<
        GetSharedOrganizationsObj[]
    >([])

    const [shareOrgsDisplayCount] = useState<number>(3)

    const [Icons, setIcons] = useState<ILogo[]>([])

    const [properties, setProperties] = useState([
        { id: TemplateProperties.ABSTRACT, label: 'Abstract', value: false },
        { id: TemplateProperties.HIDDEN, label: 'Hidden', value: false },
        { id: TemplateProperties.ENTITY_JSON, label: 'JSON', value: false },
        { id: TemplateProperties.RULESET, label: 'Rule Set', value: false },
        { id: TemplateProperties.PREVIEW, label: 'Preview', value: false },
    ])

    const [isShareOpen, setIsShareOpen] = useState<boolean>(false)

    const [refetchSharedOrgs, setRefetchSharedOrgs] = useState<boolean>(false)

    const [templatePremissions, setTemplatePremissions] =
        useState<TemplatePermissions>({})

    useEffect(() => {
        const setOptions = async () => {
            dispatch(updateLoader(true))
            const response = await Promise.all([
                fetchAllLogos(entityId, orgId),
                fetchSelectedLogo(entityId, orgId),
            ])

            const updatedLogos = response[1]
                ? response[0]?.data?.map((l) => {
                      if (l.uuid === response[1]?.uuid) {
                          return { ...l, ...{ selected: true } }
                      } else {
                          return l
                      }
                  }) || []
                : response[0]?.data ?? []
            setIcons(updatedLogos)
            dispatch(updateLoader(false))
        }
        setOptions()
    }, [])

    useEffect(() => {
        const setSharedOrgs = async () => {
            dispatch(updateLoader(true))
            const params = {
                pageNumber: 1,
                size: 10,
                searchText: '',
                filterBy: 'shared',
                sortOrder: '',
                sortBy: '',
            }
            const response = await Promise.all([
                fetchSharedOrgsByTemId(entityId, orgId, params),
            ])
            const sharedOrgsResult = response[0]?.organizations
                ? response[0].organizations
                : []

            setExsitingSharedOrgs(sharedOrgsResult)
            dispatch(updateLoader(false))
        }
        setSharedOrgs()
    }, [refetchSharedOrgs])

    useEffect(() => {
        if (navList?.length) {
            const updatedEntity = findEntityById(navList, selectedEntity?.id)
            const selectedEntityProperties = updatedEntity?.entity?.properties
            if (updatedEntity?.entity) {
                const updatedProperties: any[] = properties.map((i) => {
                    if (i.id === TemplateProperties.ABSTRACT) {
                        return {
                            ...i,
                            ...{
                                value:
                                    selectedEntityProperties?.[
                                        TemplateProperties.ABSTRACT
                                    ] || false,
                            },
                        }
                    } else if (i.id === TemplateProperties.HIDDEN) {
                        return {
                            ...i,
                            ...{
                                value:
                                    selectedEntityProperties?.[
                                        TemplateProperties.HIDDEN
                                    ] || false,
                            },
                        }
                    } else if (i.id === TemplateProperties.ENTITY_JSON) {
                        return {
                            ...i,
                            ...{
                                value:
                                    selectedEntityProperties?.[
                                        TemplateProperties.ENTITY_JSON
                                    ] || false,
                            },
                        }
                    } else if (i.id === TemplateProperties.RULESET) {
                        return {
                            ...i,
                            ...{
                                value:
                                    selectedEntityProperties?.[
                                        TemplateProperties.RULESET
                                    ] || false,
                            },
                        }
                    } else if (i.id === TemplateProperties.PREVIEW) {
                        return {
                            ...i,
                            ...{
                                value:
                                    selectedEntityProperties?.[
                                        TemplateProperties.PREVIEW
                                    ] || false,
                            },
                        }
                    } else {
                        return i
                    }
                })
                setProperties(updatedProperties)
                setTemplatePremissions(updatedEntity?.entity?.permissions)
            }
        }
    }, [navList])

    const onSelectIcon = (id: number | string) => {
        const updatedIcons = Icons.map((item) => {
            if (item.uuid === id) {
                item.selected = true
            } else {
                item.selected = false
            }
            return item
        })
        setIcons(updatedIcons)
    }

    const onclickCancelIcon = () => {
        const updatedIcons = Icons.map((item) => {
            item.selected = false
            return item
        })
        setIcons(updatedIcons)
    }

    const handleOpenSharedOrgList = () => {
        setIsShareOpen(true)
    }

    const onCloseSharePopup = () => {
        setRefetchSharedOrgs((prev) => !prev)
        setIsShareOpen(false)
    }

    const onChnageProperties = (id: any, value: any) => {
        const updatedProperites = properties?.map((i: any) => {
            if (i.id === id) {
                return { ...i, ...{ value } }
            } else {
                return { ...i }
            }
        })
        setProperties(updatedProperites)
    }

    const handleApplyClick = () => {
        dispatch(updateLoader(true))
        const selectedLogo = Icons.find((i) => i.selected)

        const templateProperties: any = {}

        properties.forEach((i) => {
            if (i.id === TemplateProperties.ABSTRACT) {
                templateProperties[TemplateProperties.ABSTRACT] = i.value
            } else if (i.id === TemplateProperties.HIDDEN) {
                templateProperties[TemplateProperties.HIDDEN] = i.value
            } else if (i.id === TemplateProperties.ENTITY_JSON) {
                templateProperties[TemplateProperties.ENTITY_JSON] = i.value
            } else if (i.id === TemplateProperties.RULESET) {
                templateProperties[TemplateProperties.RULESET] = i.value
            } else if (i.id === TemplateProperties.PREVIEW) {
                templateProperties[TemplateProperties.PREVIEW] = i.value
            }
        })

        const updatedTemplateData: any = {
            id: entityId,
            logoId: selectedLogo ? selectedLogo.uuid : '',
            properties: templateProperties,
            permissions: templatePremissions,
        }
        Promise.all([
            updateTemplate(entityId, orgId, updatedTemplateData),
        ]).then((result) => {
            if (result) {
                dispatch(getEntities(orgId))
                enqueueSnackbar(t('settingsUpdated'), {
                    variant: 'success',
                })
            }
        })
        dispatch(updateLoader(false))
    }

    return (
        <Box
            sx={{
                padding: '10px 20px 20px 20px',
                maxHeight: 'calc(100vh - 370px)',
                overflow: 'auto',
            }}
        >
            <Box>
                {isSuperAdmin ? (
                    <Box style={{ marginTop: '15px' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            id="share-template"
                            onClick={() => {
                                handleOpenSharedOrgList()
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: '600',
                                    fontSize: '14px',
                                }}
                            >
                                {t('sharedOrganizations')}
                            </Typography>
                            <Icon
                                sx={{
                                    marginLeft: '15px',
                                    cursor: 'pointer',
                                }}
                                id="share-template-button"
                            >
                                <ShareOutlined color="primary" />
                            </Icon>
                        </Box>
                        {exsitingSharedOrgs?.length ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    marginTop: '20px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {exsitingSharedOrgs
                                        ?.slice(0, shareOrgsDisplayCount)
                                        ?.map((item: any, index: number) => (
                                            <Box
                                                sx={{
                                                    background: '#E9E9E9',
                                                    padding: '10px',
                                                    marginRight: '15px',
                                                    borderRadius: '5px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                key={index}
                                                id={item.name}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: '#333',
                                                        }}
                                                    >
                                                        {item.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                </Box>
                                {exsitingSharedOrgs?.length <=
                                shareOrgsDisplayCount ? (
                                    <></>
                                ) : (
                                    <Box
                                        sx={{ cursor: 'pointer' }}
                                        id="share-template-more-icon"
                                        onClick={() => {
                                            handleOpenSharedOrgList()
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                color: `${theme.palette.primary.main}`,
                                            }}
                                        >
                                            {t('more')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
                <Box style={{ marginTop: '25px' }}>
                    <Typography
                        sx={{
                            fontWeight: '600',
                            fontSize: '14px',
                            marginBottom: '5px',
                        }}
                    >
                        {t('chooseIcon')}
                    </Typography>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap="5px"
                        sx={{ marginTop: '20px' }}
                    >
                        {Icons ? (
                            Icons.map((icon, index) => (
                                <Box
                                    sx={{
                                        padding: '6px',
                                        border: icon.selected
                                            ? '2px solid gray'
                                            : '',
                                        borderRadius: '5px',
                                        position: 'relative',
                                        // width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        margin: '0px 5px',
                                    }}
                                    key={index}
                                >
                                    {icon.selected ? (
                                        <IconButton
                                            id={icon.uuid + '-cancel-icon'}
                                            sx={{
                                                position: 'absolute',
                                                top: '-17px',
                                                right: '-17px',
                                            }}
                                            onClick={onclickCancelIcon}
                                        >
                                            <HighlightOffSharp
                                                color="error"
                                                fontSize="small"
                                            />
                                        </IconButton>
                                    ) : (
                                        <></>
                                    )}
                                    <img
                                        id={icon.uuid}
                                        src={icon.url}
                                        onClick={() => onSelectIcon(icon.uuid)}
                                    />
                                </Box>
                            ))
                        ) : (
                            <></>
                        )}
                    </Box>
                </Box>
                <Box style={{ marginTop: '25px' }}>
                    <Typography
                        sx={{
                            fontWeight: '600',
                            fontSize: '14px',
                            marginBottom: '5px',
                        }}
                    >
                        {t('properties')}
                    </Typography>
                    <Box display="flex" gap="5px" sx={{ marginTop: '20px' }}>
                        {properties?.map((item: any) => (
                            <Box key={item.id}>
                                <FormControlLabel
                                    label={item?.label}
                                    control={
                                        <Checkbox
                                            id={item.id}
                                            checked={item.value}
                                            onChange={(e, v) =>
                                                onChnageProperties(item.id, v)
                                            }
                                        />
                                    }
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
                {isSuperAdmin && (
                    <Box>
                        <SettingPermisons
                            templatePermissions={templatePremissions || {}}
                            setTemplatePremissions={setTemplatePremissions}
                        />
                    </Box>
                )}
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        id="settings-apply-button"
                        variant="contained"
                        color="primary"
                        onClick={handleApplyClick}
                    >
                        {t('apply')}
                    </Button>
                </Box>
                {isShareOpen && (
                    <ShareTemplate
                        seletedTemplate={entityId}
                        isOpen={isShareOpen}
                        onClose={() => {
                            onCloseSharePopup()
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

export default Settings
